import { createModal } from './modal.js';
import { createElement, icons } from 'lucide';
import { currLang } from '@/scripts/translation.js';
// If you're using the UMD/CDN build instead, swap the import above for:
// const { createElement, icons } = window.lucide;

/**
 * Builds the modal body: optional heading line, a primary action button,
 * optional text preview, and optional extra field. Runs fresh every time the
 * modal opens. If no custom action is supplied, the default action copies text.
 */
// shareModal.js
function buildShareContent({
  text,
  heading,
  copyLabel = currLang['CopyButton.Text'],
  copiedLabel = currLang['Copied'] + '!',
  actionLabel,
  actionDoneLabel,
  actionIcon,
  onAction,
  extraField, // optional: { label, placeholder, initialValue, buildText(value) }
  showTextPreview = true,
}) {
  const wrapper = document.createElement('div');
  wrapper.className = 'share-modal';

  if (heading) {
    const headingEl = document.createElement('p');
    headingEl.className = 'share-modal__heading';
    headingEl.textContent = heading;
    wrapper.appendChild(headingEl);
  }

  // The action always uses the latest generated text.
  let currentText = text;

  // --- primary action button -------------------------------------------
  const actionBtn = document.createElement('button');
  actionBtn.type = 'button';
  actionBtn.className = 'share-modal__action';

  const iconSlot = document.createElement('span');
  iconSlot.className = 'share-modal__action-icon';
  iconSlot.appendChild(createElement(actionIcon ?? icons.Copy, { width: 16, height: 16 }));

  const label = document.createElement('span');
  label.textContent = actionLabel ?? copyLabel;

  actionBtn.appendChild(iconSlot);
  actionBtn.appendChild(label);

  const resultBox = document.createElement('div');
  resultBox.className = 'share-modal__result';
  resultBox.hidden = true;

  const resultHeader = document.createElement('div');
  resultHeader.className = 'share-modal__result-header';

  const resultLabel = document.createElement('span');
  resultLabel.className = 'share-modal__result-label';
  resultLabel.textContent = currLang['UI.GeneratedLink'];

  const resultCopyBtn = document.createElement('button');
  resultCopyBtn.type = 'button';
  resultCopyBtn.className = 'share-modal__result-copy';
  resultCopyBtn.setAttribute('aria-label', 'Copy link');
  const resultCopyIcon = createElement(icons.Copy, { width: 14, height: 14 });
  resultCopyBtn.appendChild(resultCopyIcon);

  resultCopyBtn.addEventListener('click', async () => {
    const link = resultLink.href;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch (err) {
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      } catch {
        return;
      }
    }
    resultCopyBtn.replaceChildren(createElement(icons.Check, { width: 14, height: 14 }));
    resultCopyBtn.classList.add('share-modal__result-copy--done');
    clearTimeout(resultCopyBtn._resetTimer);
    resultCopyBtn._resetTimer = setTimeout(() => {
      resultCopyBtn.replaceChildren(createElement(icons.Copy, { width: 14, height: 14 }));
      resultCopyBtn.classList.remove('share-modal__result-copy--done');
    }, 1500);
  });

  resultHeader.appendChild(resultLabel);
  resultHeader.appendChild(resultCopyBtn);

  const resultLink = document.createElement('a');
  resultLink.className = 'share-modal__result-link';
  resultLink.target = '_blank';
  resultLink.rel = 'noopener noreferrer';

  resultBox.appendChild(resultHeader);
  resultBox.appendChild(resultLink);

  const setButtonState = ({ busy = false, done = false } = {}) => {
    actionBtn.disabled = busy;
    iconSlot.innerHTML = '';
    iconSlot.appendChild(
      createElement(done ? icons.Check : actionIcon ?? icons.Copy, { width: 16, height: 16 })
    );
    label.textContent = busy
      ? currLang['UI.Generating']
      : done
        ? actionDoneLabel ?? copiedLabel
        : actionLabel ?? copyLabel;
    actionBtn.classList.toggle('share-modal__action--done', done);
  };

  actionBtn.addEventListener('click', async () => {
    try {
      if (onAction) {
        setButtonState({ busy: true });
        const result = await onAction({ text: currentText });
        const link = typeof result === 'string' ? result : result?.link;
        if (link) {
          resultLink.href = link;
          resultLink.textContent = link;
          resultBox.hidden = false;
        }
        setButtonState({ done: true });
        return;
      }

      await navigator.clipboard.writeText(currentText);
    } catch (err) {
      if (onAction) {
        resultLink.removeAttribute('href');
        resultLink.textContent = err?.message || currLang['UI.UnableToGenerateLink'];
        resultBox.hidden = false;
        setButtonState();
        return;
      }

      try {
        const ta = document.createElement('textarea');
        ta.value = currentText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      } catch {
        setButtonState();
        return;
      }
    }

    setButtonState({ done: true });

    clearTimeout(actionBtn._resetTimer);
    actionBtn._resetTimer = setTimeout(() => {
      setButtonState();
    }, 1500);
  });

  const pre = document.createElement('pre');
  pre.className = 'share-modal__code';
  pre.textContent = currentText;

  wrapper.appendChild(actionBtn);
  wrapper.appendChild(resultBox);

  let ckEditorInstance = null;

  // --- optional extra-field toggle (Export only) ------------------------
  if (extraField) {
    const useRichText = Boolean(extraField.editor);

    const toggleRow = document.createElement('label');
    toggleRow.className = 'share-modal__toggle-row';

    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.className = 'share-modal__toggle';
    const initialValue = String(extraField.initialValue || '').trim();
    toggle.checked = Boolean(initialValue);

    const toggleText = document.createElement('span');
    toggleText.textContent = extraField.label ?? currLang['UI.AddField'];

    toggleRow.appendChild(toggle);
    toggleRow.appendChild(toggleText);

    let fieldEl;
    let editorTarget = null; // the empty node CKEditor attaches to
    let getFieldValue;

    if (useRichText) {
      fieldEl = document.createElement('div');
      fieldEl.className = 'share-modal__extra-editor';

      editorTarget = document.createElement('div');
      fieldEl.appendChild(editorTarget);

      getFieldValue = () => (ckEditorInstance ? ckEditorInstance.getData() : initialValue);
    } else {
      fieldEl = document.createElement('input');
      fieldEl.type = 'text';
      fieldEl.className = 'share-modal__extra-input';
      fieldEl.placeholder = extraField.placeholder ?? '';
      fieldEl.value = initialValue;

      getFieldValue = () => fieldEl.value;
    }
    fieldEl.hidden = !toggle.checked;

    const sync = () => {
      fieldEl.hidden = !toggle.checked;
      currentText = toggle.checked ? extraField.buildText(getFieldValue()) : text;
      if (showTextPreview) {
        pre.textContent = currentText;
      }
    };

    toggle.addEventListener('change', () => {
      // lazily boot the editor the first time the box is checked
      if (useRichText && toggle.checked && !ckEditorInstance) {
        const { ClassicEditor, config } = extraField.editor;
        ClassicEditor.create(editorTarget, config)
          .then((editor) => {
            ckEditorInstance = editor;
            if (initialValue) {
              editor.setData(initialValue);
            }
            editor.model.document.on('change:data', sync);
            sync();
          })
          .catch((err) => console.error('Failed to load instruction editor:', err));
      }
      sync();
    });

    if (!useRichText) {
      fieldEl.addEventListener('input', sync);
    }

    wrapper.appendChild(toggleRow);
    wrapper.appendChild(fieldEl);

    if (useRichText && toggle.checked) {
      const { ClassicEditor, config } = extraField.editor;
      ClassicEditor.create(editorTarget, config)
        .then((editor) => {
          ckEditorInstance = editor;
          editor.setData(initialValue);
          editor.model.document.on('change:data', sync);
          sync();
        })
        .catch((err) => console.error('Failed to load instruction editor:', err));
    }
    sync();
  }

  if (showTextPreview) {
    wrapper.appendChild(pre);
  }

  wrapper._destroy = () => {
    if (ckEditorInstance) {
      const editor = ckEditorInstance;
      ckEditorInstance = null;
      editor.destroy().catch(() => {});
    }
  };

  return wrapper;
}

export function createShareModal(getContent) {
  let activeContent = null;

  const teardown = () => {
    activeContent?._destroy?.();
    activeContent = null;
  };

  return createModal({
    title: () => getContent().title,
    closable: true,
    size: 'lg',
    content: () => {
      teardown();
      activeContent = buildShareContent(getContent());
      return activeContent;
    },
    onClose: teardown,
  });
}
